import { useCallback, useRef } from 'react';

import { useAuth } from '@/context/auth';

/**
 * EWM-548: Tenant logo/CSS assets are served from PRIVATE blob containers via short-lived
 * (~24h) SAS URLs minted server-side. When a stored SAS expires, the browser gets a 403 and
 * the asset fails to load (broken logo / lost theme).
 *
 * This hook returns a one-shot error handler that re-fetches the tenant (which mints a fresh
 * SAS URL) via {@link useAuth}'s `refreshTenant`. It guards against refresh loops: it only
 * triggers a single refresh until {@link reset} is called (e.g. when a new URL successfully
 * loads), so a permanently-broken asset never spins the network.
 */
export const useTenantAssetRefresh = () => {
  const { refreshTenant } = useAuth();
  const hasRefreshed = useRef(false);

  const onAssetError = useCallback(() => {
    if (hasRefreshed.current) {
      return;
    }
    hasRefreshed.current = true;
    void refreshTenant();
  }, [refreshTenant]);

  const reset = useCallback(() => {
    hasRefreshed.current = false;
  }, []);

  return { onAssetError, reset };
};
